import { flatMap, map, size, some, uniq } from "lodash";
import { Layout } from "../model";
import { WizardTemplate } from "./model";
import { WizardCard } from "./model/card";
import { WizardConfigurationLoopField } from "./model/configuration";
import {
  LoopCardsSection,
  WizardCardRow,
  WizardLoopRow,
  WizardRow,
  WizardTitleRow,
} from "./model/section";
import {
  CardsSection,
  DescriptionSection,
  WizardTemplateSection,
} from "./model/section";

export function isDescriptionSection(
  section: WizardTemplateSection
): section is DescriptionSection {
  return section.type === "description";
}

export function isLoopCardsSection(
  section: WizardTemplateSection
): section is LoopCardsSection {
  return section.type === "loop-cards";
}

function getMandatoryFieldsFromSection(section: WizardTemplateSection) {
  if (isDescriptionSection(section)) {
    return ["description"];
  } else if (isLoopCardsSection(section)) {
    return [...(section.clause || []), section.loop];
  } else {
    return [...((section.conditions && section.conditions.with) || [])].concat(
      ...(section.clause || [])
    );
  }
}

export function getMandatoryFieldsFromSections(
  sections: WizardTemplateSection[]
) {
  return uniq(flatMap(map(sections, getMandatoryFieldsFromSection)));
}

export function hasDescriptionSectionFromWizardTemplate(
  template: WizardTemplate
): boolean {
  return some(template.sections, (section) => isDescriptionSection(section));
}

export class WizardSectionBuilder {
  buildDescriptionSection(): DescriptionSection {
    return {
      type: "description",
    };
  }
  buildCardSection(
    rows: WizardRow[],
    clause?: LoopCardsSection["clause"],
    conditions?: CardsSection["conditions"]
  ): CardsSection {
    return {
      type: "cards",
      rows,
      clause,
      conditions,
    };
  }
  buildLoopCardsSection(
    rows: WizardLoopRow[],
    loop: WizardConfigurationLoopField,
    clause?: LoopCardsSection["clause"]
  ): LoopCardsSection {
    return {
      type: "loop-cards",
      loop,
      rows,
      clause,
    };
  }
  static getInstance() {
    return new WizardSectionBuilder();
  }
}

export function isWizardCardRow(row: WizardRow): row is WizardCardRow {
  return row.type === "card";
}

export function isWizardTitleRow(row: WizardRow): row is WizardTitleRow {
  return row.type === "title";
}

export function getWizardTitleRow(
  titles: string[],
  style: WizardTitleRow["style"],
  position?: "right" | "left"
): WizardTitleRow {
  const length = size(titles);
  if (length === 1) {
    return {
      layout: Layout.Row_1,
      type: "title",
      style,
      titles: titles as [string],
    };
  } else if (length === 2) {
    return {
      layout:
        position === "right"
          ? Layout.Row_1_2
          : position === "left"
          ? Layout.Row_2_1
          : Layout.Row_2,
      type: "title",
      style,
      titles: titles as [string, string],
    };
  } else {
    return {
      layout: Layout.Row_3,
      type: "title",
      style,
      titles: titles as [string, string, string],
    };
  }
}

export function getWizardCardRow(
  cards: WizardCard[],
  position?: "right" | "left"
): WizardCardRow {
  const length = size(cards);
  if (length === 1) {
    return {
      layout: Layout.Row_1,
      type: "card",
      cards: cards as [WizardCard],
    };
  } else if (length === 2) {
    return {
      layout:
        position === "right"
          ? Layout.Row_1_2
          : position === "left"
          ? Layout.Row_2_1
          : Layout.Row_2,
      type: "card",
      cards: cards as [WizardCard, WizardCard],
    };
  } else {
    return {
      layout: Layout.Row_3,
      type: "card",
      cards: cards as [WizardCard, WizardCard, WizardCard],
    };
  }
}
